import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle, CreditCard, Copy, Phone, Building, User, Wallet } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getImageUrl, formatPrice } from '../utils/helpers';
import { OrderFollowUp } from '../components/checkout/OrderFollowUp';

const ThankYou = ({ orderOverride }) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const order = orderOverride || state?.order;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copiado', description: 'Información copiada al portapapeles' });
  };

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center" data-testid="thank-you-empty">
        <Card className="p-12 text-center max-w-md">
          <h2 className="text-2xl font-bold mb-2">No hay información de pedido</h2>
          <p className="text-gray-600 mb-6">Parece que llegaste aquí directamente.</p>
          <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600 text-white">Ir al inicio</Button>
        </Card>
      </div>
    );
  }

  const paymentInfo = order.payment_info;
  const payMethod = paymentInfo?.method;
  const isPaid = order.status === 'paid' || paymentInfo?.status === 'confirmed';

  return (
    <div className="min-h-screen bg-gray-100 py-8" data-testid="thank-you-page">
      <div className="max-w-3xl mx-auto px-4">
        <Card className="p-4 sm:p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <CheckCircle className={`w-20 h-20 mx-auto mb-4 ${isPaid ? 'text-green-500' : 'text-amber-500'}`} />
            <h1 className="text-3xl font-bold mb-2" data-testid="thank-you-title"
              style={{ color: isPaid ? '#15803d' : '#b45309' }}>
              {isPaid ? '¡Pago Exitoso!' : '¡Pedido Creado!'}
            </h1>
            <p className="text-gray-600" data-testid="thank-you-order-id">
              Número de pedido: <span className="font-mono font-bold">{order.order_id?.substring(0, 8).toUpperCase()}</span>
            </p>
          </div>

          {/* Card payment confirmed */}
          {payMethod === 'card' && isPaid && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6" data-testid="card-payment-confirmed">
              <div className="flex items-center gap-3 mb-2">
                <CreditCard className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-bold text-green-800">Pago con tarjeta confirmado</h2>
              </div>
              <p className="text-green-700 text-sm">
                Tu pago de {formatPrice(order.total)} ha sido procesado exitosamente.
                {paymentInfo.mp_payment_id && <span className="block mt-1 text-xs text-green-500">ID de pago: {paymentInfo.mp_payment_id}</span>}
              </p>
            </div>
          )}

          {/* Wallet payment confirmed */}
          {payMethod === 'wallet' && isPaid && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6" data-testid="wallet-payment-confirmed">
              <div className="flex items-center gap-3 mb-2">
                <Wallet className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-bold text-green-800">Pago con billetera confirmado</h2>
              </div>
              <p className="text-green-700 text-sm">
                Se debitaron {formatPrice(order.total)} de tu billetera. Tu pedido ya está en camino.
              </p>
            </div>
          )}

          {/* P2P instructions */}
          {payMethod === 'p2p' && !isPaid && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6" data-testid="payment-instructions">
              <h2 className="text-xl font-bold text-yellow-800 mb-4 flex items-center">
                <CreditCard className="w-6 h-6 mr-2" />
                Instrucciones de Pago
              </h2>
              <p className="text-yellow-700 mb-4">
                Realiza la transferencia con los siguientes datos y luego confirma tu pago:
              </p>
              <div className="bg-white rounded-lg p-4 space-y-3">
                {paymentInfo.bank_name && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Building className="w-5 h-5 text-gray-500 mr-2" />
                      <span className="text-gray-600">Banco:</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-semibold">{paymentInfo.bank_name}</span>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(paymentInfo.bank_name)}><Copy className="w-4 h-4" /></Button>
                    </div>
                  </div>
                )}
                {paymentInfo.account_type && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CreditCard className="w-5 h-5 text-gray-500 mr-2" />
                      <span className="text-gray-600">Tipo de cuenta:</span>
                    </div>
                    <span className="font-semibold capitalize">{paymentInfo.account_type}</span>
                  </div>
                )}
                {paymentInfo.account_number && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CreditCard className="w-5 h-5 text-gray-500 mr-2" />
                      <span className="text-gray-600">Número de cuenta:</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-semibold font-mono">{paymentInfo.account_number}</span>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(paymentInfo.account_number)}><Copy className="w-4 h-4" /></Button>
                    </div>
                  </div>
                )}
                {paymentInfo.account_holder && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-gray-500 mr-2" />
                      <span className="text-gray-600">Titular:</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-semibold">{paymentInfo.account_holder}</span>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(paymentInfo.account_holder)}><Copy className="w-4 h-4" /></Button>
                    </div>
                  </div>
                )}
                {paymentInfo.phone_number && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Phone className="w-5 h-5 text-gray-500 mr-2" />
                      <span className="text-gray-600">Teléfono:</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-semibold">{paymentInfo.phone_number}</span>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(paymentInfo.phone_number)}><Copy className="w-4 h-4" /></Button>
                    </div>
                  </div>
                )}
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold">Total a pagar:</span>
                    <span className="text-2xl font-bold text-orange-500">{formatPrice(order.total)}</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-yellow-700 mt-4">
                * Reporta tu transferencia abajo. El pago se confirma después de su verificación.
              </p>
            </div>
          )}

          <OrderFollowUp key={order.order_id} order={order} />
          <p className="text-sm text-gray-600 mb-4" data-testid="order-current-status">Estado: {({ pending_payment: 'Pendiente de pago', paid: 'Pagado', shipped: 'Enviado', delivered: 'Entregado', cancelled: 'Cancelado', searching_driver: 'Buscando repartidor', driver_assigned: 'Repartidor asignado', in_transit: 'En camino' })[order.status] || order.status}</p>
          {/* Order items */}
          <div className="border-t pt-6">
            <h3 className="font-bold mb-4">Resumen del pedido</h3>
            <div className="space-y-3" data-testid="thank-you-items">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 text-sm">
                  <img src={getImageUrl(item.product_image)} alt={item.product_title}
                    className="w-12 h-12 object-cover rounded"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/48'; }} />
                  <div className="flex-1">
                    <p className="font-medium">{item.product_title}</p>
                    <p className="text-gray-500">Cantidad: {item.quantity}</p>
                  </div>
                  <p className="font-semibold">{formatPrice(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <Button variant="outline" className="flex-1" onClick={() => navigate('/')} data-testid="continue-shopping-btn">
              Seguir comprando
            </Button>
            <Button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white" onClick={() => navigate(order.guest ? `/guest-order/${order.order_id}` : '/my-purchases')} data-testid="view-purchases-btn">
              {order.guest ? 'Ver mi pedido' : 'Ver mis compras'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ThankYou;
